import { Course, CustomTimeBlock } from '../types';

export function timeToMinutes(t: string): number | null {
  const m = t.trim().match(/^(\d{1,2}):(\d{2})\s*([APap][Mm])$/);
  if (!m) return null;
  let hh = parseInt(m[1], 10), mm = parseInt(m[2], 10);
  const ampm = m[3].toUpperCase();
  if (ampm === "PM" && hh !== 12) hh += 12;
  if (ampm === "AM" && hh === 12) hh = 0;
  return hh * 60 + mm;
}

/** Banner lab rows often omit the opening <tr> after </tr>. */
export function normalizeScheduleHtml(html: string): string {
  return html.replace(/<\/tr>\s*(<td\b)/gi, '</tr>\n<tr>$1');
}

export function parseDays(dstr: string): Set<string> {
  const set = new Set<string>();
  if (!dstr) return set;
  const DAYS = ["M", "T", "W", "R", "F"];
  for (const ch of dstr.replace(/\s+/g, '')) {
    if (DAYS.includes(ch)) set.add(ch);
  }
  return set;
}

export function colorFor(crn: string): string {
  let x = 0;
  for (let i = 0; i < crn.length; i++) {
    x = (x * 131 + crn.charCodeAt(i)) % 360;
  }
  return `hsl(${x}, 65%, 55%)`;
}

export function formatFetchedAt(iso?: string | null): string | null {
  if (!iso) return null;
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return null;
  return date.toLocaleString(undefined, {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
}

export type CompactCourse = Omit<Course, '__color' | '__bg' | 'isCustomBlock' | 'customCrn' | 'customField' | 'unavailable'>;

export interface ScheduleMeta {
  fetchedAt: string;
  year: number;
  term: string;
  termCode: string;
}

export interface ScheduleSnapshot extends ScheduleMeta {
  courses: CompactCourse[];
  online: CompactCourse[];
}

export function hydrateCourse(row: CompactCourse): Course {
  const color = colorFor(row.CRN);
  return {
    ...row,
    __color: color,
    __bg: color.replace(/^hsl\(([^)]+)\)$/, 'hsla($1, 0.22)'),
  };
}

export function hydrateSnapshot(snapshot: ScheduleSnapshot): { courses: Course[]; online: Course[] } {
  return {
    courses: (snapshot.courses || []).map(hydrateCourse),
    online: (snapshot.online || []).map(hydrateCourse),
  };
}

function sameSection(a: Course, b: Course): boolean {
  return a.CRN === b.CRN && a.Subject === b.Subject && a.Course === b.Course;
}

export function markUnavailable(courses: Course[]): Course[] {
  return courses.map((course) => (course.isCustomBlock ? course : { ...course, unavailable: true }));
}

/**
 * Re-link saved My Schedule rows to the fresh catalog. Rows whose section is gone
 * are kept (flagged `unavailable`) so a catalog refresh never deletes a student's picks.
 */
export function rematchSavedCourses(saved: Course[], catalog: Course[]): Course[] {
  const remaining = [...catalog];
  const result: Course[] = [];
  for (const course of saved) {
    if (course.isCustomBlock) {
      result.push(course);
      continue;
    }
    let idx = remaining.findIndex((candidate) =>
      sameSection(candidate, course) &&
      candidate.Days === course.Days &&
      candidate.StartMin === course.StartMin &&
      candidate.EndMin === course.EndMin
    );
    if (idx < 0) {
      idx = remaining.findIndex((candidate) => sameSection(candidate, course));
    }
    if (idx >= 0) {
      result.push(remaining.splice(idx, 1)[0]);
    } else {
      result.push({ ...course, unavailable: true });
    }
  }
  return result;
}

export function countNewlyUnavailable(before: Course[], after: Course[]): number {
  const wasAvailable = new Set(before.filter((c) => !c.unavailable && !c.isCustomBlock).map((c) => c.CRN));
  return new Set(after.filter((c) => c.unavailable && wasAvailable.has(c.CRN)).map((c) => c.CRN)).size;
}

/**
 * True when the incoming snapshot is a different semester than the local catalog.
 * Older saves have no term code, so fall back to comparing the catalogs' sections.
 */
export function isTermChange(
  localTermCode: string | null | undefined,
  localCourses: Course[] | undefined,
  snapshotTermCode: string | undefined,
  snapshotCourses: Course[],
): boolean {
  if (localTermCode && snapshotTermCode) return localTermCode !== snapshotTermCode;
  if (!localCourses || localCourses.length === 0) return false;
  const incoming = new Set(snapshotCourses.map((c) => `${c.CRN}|${c.Subject}|${c.Course}`));
  const localKeys = new Set(localCourses.map((c) => `${c.CRN}|${c.Subject}|${c.Course}`));
  let shared = 0;
  localKeys.forEach((key) => {
    if (incoming.has(key)) shared += 1;
  });
  return shared / localKeys.size < 0.5;
}

function withCacheBust(url: string, bust: boolean): string {
  if (!bust) return url;
  const sep = url.includes('?') ? '&' : '?';
  return `${url}${sep}t=${Date.now()}`;
}

async function fetchJson(url: string): Promise<any | null> {
  try {
    const response = await fetch(url);
    if (!response.ok) return null;
    return await response.json();
  } catch (error) {
    console.warn(`Failed to fetch ${url}:`, error);
    return null;
  }
}

export async function fetchScheduleMeta(cacheBust = false): Promise<ScheduleMeta | null> {
  const meta = await fetchJson(withCacheBust('/api/schedule/meta', cacheBust));
  if (meta && typeof meta.fetchedAt === 'string') {
    return {
      fetchedAt: meta.fetchedAt,
      year: meta.year,
      term: meta.term,
      termCode: meta.termCode,
    };
  }
  return null;
}

export async function fetchScheduleSnapshot(cacheBust = false): Promise<ScheduleSnapshot> {
  const remote = await fetchJson(withCacheBust('/api/schedule', cacheBust));
  const local = remote || await fetchJson(withCacheBust('/schedule-snapshot.json', cacheBust));
  if (!local || !Array.isArray(local.courses)) {
    throw new Error('Could not load the course schedule. Please refresh and try again.');
  }
  return {
    fetchedAt: local.fetchedAt,
    year: local.year,
    term: local.term,
    termCode: local.termCode,
    courses: local.courses,
    online: local.online || [],
  };
}

export function catalogIsStale(localFetchedAt: string | null | undefined, remoteFetchedAt: string): boolean {
  if (!localFetchedAt) return true;
  const local = Date.parse(localFetchedAt);
  const remote = Date.parse(remoteFetchedAt);
  if (Number.isNaN(remote)) return false;
  if (Number.isNaN(local)) return true;
  return remote > local;
}

export function parseHtmlTable(html: string): { courses: Course[]; online: Course[] } {
  console.log('=== parseHtmlTable START ===');
  
  const parser = new DOMParser();
  const doc = parser.parseFromString(normalizeScheduleHtml(html), 'text/html');
  const table = doc.querySelector('table.dataentrytable');
  
  if (!table) {
    throw new Error('No valid schedule table found');
  }

  const courses: Course[] = [];
  const online: Course[] = [];
  let currentSubject = '', currentCourse = '', currentTitle = '';
  let lastCRN = '', lastInstructor = '', lastColor = '', lastBg = '';

  // Pre-compile regex patterns for better performance
  const subjectPattern = /^([A-Z]+)\s*-\s*(.+)$/;
  // Updated to handle course numbers with letters and any length (e.g., C1000, 020, 1234)
  const coursePattern = /^([A-Z]+)\s+([A-Z]?\d+[A-Z]?)\s+-\s+(.+?)(?:\s+Lecture)?(?:\s+Lab)?$/;
  const timePattern = /(\d{1,2}:\d{2}[ap]m)\s*-\s*(\d{1,2}:\d{2}[ap]m)/i;
  const dayPattern = /^[MTWRF]$/;

  const rows = table.querySelectorAll('tr');
  console.log(`Total rows to process: ${rows.length}`);
  
  for (const row of Array.from(rows)) {
    // Subject header: <td colspan="23" class="deheader"><b><font...>ACCT - Accounting
    const subjHeader = row.querySelector('td.deheader b font[color="DARKBLUE"]');
    if (subjHeader) {
      const text = subjHeader.textContent?.trim() || '';
      const m = text.match(subjectPattern);
      if (m) {
        currentSubject = m[1];
      }
      continue;
    }

    // Course header: ACCT 001 - Financial Accounting
    const courseHeader = row.querySelector('td.deheader');
    if (courseHeader && courseHeader.textContent?.includes(' - ')) {
      const text = courseHeader.textContent.trim();
      const m = text.match(coursePattern);
      if (m) {
        currentSubject = m[1];
        currentCourse = m[2]; // Keep original course number format (e.g., C1000, 020, 1234)
        currentTitle = m[3].trim();
      }
      continue;
    }

    const cells = row.querySelectorAll('td');
    if (cells.length < 10) continue;

    // Check if this is a continuation row (no CRN, has colspan="3" in first cell)
    const firstCell = cells[0];
    const isContinuation = firstCell && (firstCell.getAttribute('colspan') || firstCell.getAttribute('COLSPAN')) === '3' && !row.querySelector('a[href*="p_course_popup"]');

    let crn, instructor, color, bg;
    let dayStartIndex = 3; // Default day column start index
    let timeIndex = 10; // Default time column index
    let locationIndex = 12; // Default location column index
    let campusIndex = 13; // Default campus column index

    if (isContinuation) {
      // Continuation row: reuse last CRN, instructor, color
      if (!lastCRN) continue; // Skip if no previous CRN
      crn = lastCRN;
      instructor = lastInstructor;
      color = lastColor;
      bg = lastBg;
      // After colspan="3", days are at cells[1-5], time at cells[8], location at cells[10], campus at cells[11]
      dayStartIndex = 1;
      timeIndex = 8;
      locationIndex = 10;
      campusIndex = 11;
    } else {
      // Main data row with CRN
      const crnLink = row.querySelector('a[href*="p_course_popup"]');
      if (!crnLink) continue;

      crn = crnLink.textContent?.trim() || '';
      instructor = cells[20]?.textContent?.trim() || '';
      color = colorFor(crn);
      bg = color.replace(/^hsl\(([^)]+)\)$/, 'hsla($1, 0.22)');

      // Save for potential continuation rows
      lastCRN = crn;
      lastInstructor = instructor;
      lastColor = color;
      lastBg = bg;
    }

    // Days: extract from appropriate column range
    let days = '';
    const dayEnd = Math.min(dayStartIndex + 5, cells.length);
    for (let i = dayStartIndex; i < dayEnd; i++) {
      const txt = cells[i]?.textContent?.trim();
      if (txt && dayPattern.test(txt)) days += txt;
    }

    // Time
    const timeText = cells[timeIndex]?.textContent?.trim() || '';
    const tm = timeText.match(timePattern);
    if (!tm) continue;

    const startMin = timeToMinutes(tm[1]);
    const endMin = timeToMinutes(tm[2]);
    if (startMin == null || endMin == null) continue;

    // Location and Campus
    const location = cells[locationIndex]?.textContent?.trim() || '';
    const campus = cells[campusIndex]?.textContent?.trim() || '';

    // Units (Credits) - index 2 for main rows, not available for continuation rows
    const units = isContinuation ? 0 : parseFloat(cells[2]?.textContent?.trim() || '0');
    
    // Enrollment data - lab sections don't have their own enrollment data
    let capacity, actual, remaining, waitCap, waitAct, waitRem;
    
    if (isContinuation) {
      // Lab sections inherit enrollment data from the lecture section
      // They don't have individual enrollment data, so we use the lecture section's data
      capacity = 0;
      actual = 0;
      remaining = 0;
      waitCap = 0;
      waitAct = 0;
      waitRem = 0;
    } else {
      // Lecture sections have their own enrollment data
      // Status, CRN, credits, 7 day cells, time, date, location, campus, then enrollment
      const baseIndex = 14;
      capacity = parseInt(cells[baseIndex]?.textContent?.trim() || '0', 10);
      actual = parseInt(cells[baseIndex + 1]?.textContent?.trim() || '0', 10);
      remaining = parseInt(cells[baseIndex + 2]?.textContent?.trim() || '0', 10);
      waitCap = parseInt(cells[baseIndex + 3]?.textContent?.trim() || '0', 10);
      waitAct = parseInt(cells[baseIndex + 4]?.textContent?.trim() || '0', 10);
      waitRem = parseInt(cells[baseIndex + 5]?.textContent?.trim() || '0', 10);
    }

    const courseEntry: Course = {
      Subject: currentSubject,
      Course: currentCourse,
      CRN: crn,
      Title: currentTitle,
      Instructor: instructor,
      Location: location,
      Campus: campus,
      Units: units,
      Days: days,
      DispTime: timeText,
      StartMin: startMin,
      EndMin: endMin,
      Capacity: capacity,
      Actual: actual,
      Remaining: remaining,
      WaitCap: waitCap,
      WaitAct: waitAct,
      WaitRem: waitRem,
      __color: color,
      __bg: bg,
      isLabSection: isContinuation,
    };
    
    // Check if this is an online course (no days or location contains "Online")
    if (!days || days.length === 0 || location.toLowerCase().includes('online')) {
      online.push(courseEntry);
    } else {
      courses.push(courseEntry);
    }
    
  }
  
  console.log(`=== parseHtmlTable END: ${courses.length} scheduled courses, ${online.length} online courses ===`);
  return { courses, online };
}

// Check if a string is encoded schedule data
export function isEncodedScheduleData(text: string): boolean {
  try {
    // Try to decode and check for SSB type
    const jsonString = decodeURIComponent(escape(atob(text)));
    const data = JSON.parse(jsonString);
    return data.t === 'ssb';
  } catch (error) {
    return false;
  }
}

// Decode compact schedule data
export function decodeScheduleData(encodedString: string): { c: any[], o: any[], b?: any[] } | null {
  try {
    // Decode base64
    const decoded = atob(encodedString);
    let data;
    
    // Try direct JSON parse first (new format)
    try {
      data = JSON.parse(decoded);
    } catch {
      // Try with URI decoding (legacy format)
      const jsonString = decodeURIComponent(escape(decoded));
      data = JSON.parse(jsonString);
    }
    
    // Check if it's a valid SSB schedule
    if (data.t !== 'ssb') {
      throw new Error('Invalid schedule data format');
    }
    
    // Return course identifiers and custom blocks - courses will be matched from available courses
    return { c: data.c, o: data.o, b: data.b || [] };
  } catch (error) {
    console.error('Error decoding schedule data:', error);
    return null;
  }
}

export function encodeCustomBlockForShare(block: CustomTimeBlock): any[] {
  const encoded: any[] = [block.id, block.title, block.days, block.times, block.color];
  const extras: Record<string, string> = {};
  if (block.instructor) extras.instructor = block.instructor;
  if (block.crn) extras.crn = block.crn;
  if (block.location) extras.location = block.location;
  if (block.campus) extras.campus = block.campus;
  if (block.customField) extras.customField = block.customField;
  if (Object.keys(extras).length > 0) {
    encoded.push(extras);
  }
  return encoded;
}

export function decodeCustomBlockFromShare(blockArray: any[]): CustomTimeBlock {
  const extras = blockArray[5] && typeof blockArray[5] === 'object' && !Array.isArray(blockArray[5])
    ? blockArray[5]
    : {};
  return {
    id: blockArray[0],
    title: blockArray[1],
    days: blockArray[2],
    times: blockArray[3],
    color: blockArray[4],
    ...(extras.instructor ? { instructor: extras.instructor } : {}),
    ...(extras.crn ? { crn: extras.crn } : {}),
    ...(extras.location ? { location: extras.location } : {}),
    ...(extras.campus ? { campus: extras.campus } : {}),
    ...(extras.customField ? { customField: extras.customField } : {}),
  };
}
